<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        
        <div class="d-flex align-center mb-4">
          <v-icon left color="primary" class="mr-4" large>mdi-account-supervisor</v-icon>
          <div>
            <h1 class="text-left">
              {{ user && user.role === 'Manager' ? 'Manage My Teams' : 'User Management' }}
            </h1>
            <p class="text--secondary mb-0">
              {{ user && user.role === 'Manager' 
                ? 'Manage users within your assigned groups' 
                : 'Complete user management and system administration' }}
            </p>
          </div>
        </div>

        <!-- Manager scope notification -->
        <v-alert
          v-if="user && user.role === 'Manager'"
          type="info"
          outlined
          class="mb-4"
        >
          <v-row align="center">
            <v-col class="grow">
              <strong>Manager View:</strong> You are managing {{ user.groups ? user.groups.length : 0 }} group(s): 
              <span class="font-weight-medium">
                {{ user.groups ? user.groups.map(g => getGroupName(g)).join(', ') : 'None' }}
              </span>
            </v-col>
            <v-col class="shrink">
              <v-icon>mdi-account-group</v-icon>
            </v-col>
          </v-row>
        </v-alert>

        <!-- User Statistics -->
        <v-row class="mb-4">
          <v-col cols="3">
            <v-card color="blue lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ userStats.total }}</div>
                <div>{{ user && user.role === 'Manager' ? 'Team Members' : 'Total Users' }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="grey lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ userStats.inactive }}</div>
                <div>Inactive (No Role)</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="green lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ userStats.active }}</div>
                <div>Active (Has Role)</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="red lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ userStats.admins }}</div>
                <div>Admins</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Users Table -->
        <v-card>
          <v-card-title>
            All Users
            <v-spacer></v-spacer>
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Search users..."
              single-line
              hide-details
              class="mr-4"
              style="max-width: 300px;"
            ></v-text-field>
            <v-btn color="primary" @click="showInviteDialog = true">
              <v-icon left>mdi-account-plus</v-icon>
              Invite User
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <!-- Search field -->
            <v-text-field
              v-model="userSearch"
              prepend-inner-icon="mdi-magnify"
              label="Search users..."
              class="mb-4"
              outlined
              clearable
            ></v-text-field>
            
            <v-data-table
              :headers="userHeaders"
              :items="filteredUsers"
              :loading="loadingUsers"
              class="elevation-1"
            >


              <template v-slot:item.division="{ item }">
                <div v-if="getUserDivisions(item).length > 0">
                  <v-chip
                    v-for="divisionId in getUserDivisions(item)"
                    :key="divisionId"
                    small
                    class="mr-1 mb-1"
                  >
                    {{ getDivisionName(divisionId) }}
                  </v-chip>
                </div>
                <span v-else class="text--disabled">Not set</span>
              </template>

              <template v-slot:item.group="{ item }">
                <div v-if="getUserGroups(item).length > 0">
                  <v-chip
                    v-for="groupId in getUserGroups(item)"
                    :key="groupId"
                    small
                    class="mr-1 mb-1"
                  >
                    {{ getGroupName(groupId) }}
                  </v-chip>
                </div>
                <span v-else class="text--disabled">Not set</span>
              </template>

              <template v-slot:item.role="{ item }">
                <v-chip
                  :color="getRoleColor(item.role)"
                  small
                >
                  {{ getRoleName(item.role) }}
                </v-chip>
              </template>

              <template v-slot:item.last_login="{ item }">
                <v-tooltip v-if="item.last_login" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="text--secondary">
                      {{ formatDate(item.last_login) }}
                    </span>
                  </template>
                  <span>{{ formatExactDate(item.last_login) }}</span>
                </v-tooltip>
                <span v-else class="text--disabled">Never</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <!-- Edit/View icon -->
                <v-icon 
                  small 
                  class="mr-2" 
                  @click="editUser(item)"
                  :color="user && item.email === user.email ? 'blue' : 'grey'"
                >
                  {{ user && item.email === user.email ? 'mdi-eye' : 'mdi-pencil' }}
                </v-icon>
                
                <!-- Activate/Deactivate icon - hidden for your own row -->
                <v-icon 
                  v-if="!(user && item.email === user.email)"
                  small 
                  @click="deactivateUser(item)"
                >
                  {{ item.role && item.role !== '' ? 'mdi-account-off' : 'mdi-account-plus' }}
                </v-icon>
                
                <!-- Self indicator tooltip -->
                <v-tooltip v-else bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="blue" v-bind="attrs" v-on="on">
                      mdi-account-circle
                    </v-icon>
                  </template>
                  <span>This is your account</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

     <!-- Invite User Dialog -->
     <v-dialog v-model="showInviteDialog" max-width="700px">
      <v-card>
        <v-card-title>Invite New User</v-card-title>
        <v-card-text>
          <v-form ref="inviteForm" v-model="inviteFormValid">
            <v-text-field
              v-model="inviteForm.email"
              label="Email"
              :rules="[rules.required, rules.email]"
              required
            ></v-text-field>
            
            <v-select
              v-model="inviteForm.role"
              :items="getAvailableRolesForUser(user)"
              item-text="name"
              item-value="id"
              label="Role"
              :rules="[rules.required]"
              @change="onRoleChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
            
            <v-select
              v-model="inviteForm.divisions"
              :items="availableDivisions"
              item-text="name"
              item-value="id"
              label="Divisions"
              multiple
              chips
              :rules="[rules.required]"
              @change="onDivisionsChange"
            ></v-select>

            <v-select
              v-model="inviteForm.groups"
              :items="availableGroupsForDivisions"
              item-text="name"
              item-value="id"
              label="Groups"
              multiple
              chips
              :rules="[rules.required]"
              :disabled="!inviteForm.divisions || inviteForm.divisions.length === 0"
              @change="onGroupsChange"
            ></v-select>

            <!-- Manager Status automatically determined by Role -->
            <v-alert
              v-if="inviteForm.role === 'Manager'"
              type="info"
              outlined
              class="mt-4"
            >
              <strong>Manager Permissions:</strong> This user will automatically have manager permissions for all their assigned groups.
            </v-alert>

            <v-expansion-panels class="mt-4">

              <v-expansion-panel>
                <v-expansion-panel-header>Tool Permissions</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-if="inviteForm.role === 'Manager' && availableToolsForGroups.length > 0" class="mt-2">
                    <v-alert type="success" outlined>
                      <strong>Manager Tool Access:</strong> This user will automatically have access to all tools for their assigned groups.
                    </v-alert>
                    <div v-for="tool in availableToolsForGroups" :key="tool.id" class="mb-2 d-flex align-center">
                      <v-icon color="green" class="mr-3">
                        mdi-check-circle
                      </v-icon>
                      <span>{{ tool.name }}</span>
                      <v-chip 
                        color="green" 
                        small 
                        class="ml-auto"
                      >
                        Auto-Granted
                      </v-chip>
                    </div>
                  </div>
                  <div v-else-if="availableToolsForGroups.length > 0" class="mt-2">
                    <v-alert type="info" outlined class="mb-3">
                      <strong>Individual Tool Access:</strong> Grant specific tool permissions for this {{ inviteForm.role || 'user' }}.
                    </v-alert>
                    <div v-for="tool in availableToolsForGroups" :key="tool.id" class="mb-2">
                      <v-row align="center">
                        <v-col cols="8">
                          {{ tool.name }}
                        </v-col>
                        <v-col cols="4">
                          <v-switch
                            v-model="inviteForm.toolPermissions[tool.id].access"
                            label="Access"
                            dense
                          ></v-switch>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                  <div v-else class="text--secondary">
                    Select groups to see available tools
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showInviteDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="inviteUser" :loading="invitingUser">Invite</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit User Dialog -->
    <v-dialog v-model="showEditDialog" max-width="700px">
      <v-card>
        <v-card-title>
          {{ isEditingSelf ? 'View Your Profile' : 'Edit User' }}: {{ editingUser?.email }}
        </v-card-title>
        <v-card-text>
          <!-- Security notice for self-editing -->
          <v-alert
            v-if="isEditingSelf"
            type="warning"
            outlined
            prominent
            class="mb-4"
          >
            <v-row align="center">
              <v-col class="grow">
                <strong>Security Notice:</strong> You are viewing your own profile. 
                All fields are read-only for security reasons. Contact an administrator to make changes.
              </v-col>
              <v-col class="shrink">
                <v-icon>mdi-shield-lock</v-icon>
              </v-col>
            </v-row>
          </v-alert>
          
          <v-form ref="editForm" v-model="editFormValid" v-if="editingUser">
            <!-- Role field - read-only for self-editing, dropdown for others -->
            <v-text-field
              v-if="isEditingSelf"
              :value="getRoleName(editingUser.role)"
              label="Role (cannot edit your own role)"
              readonly
              outlined
              prepend-icon="mdi-shield-account"
              hint="Contact another admin to change your role"
              persistent-hint
            ></v-text-field>
            
            <v-select
              v-else
              v-model="editingUser.role"
              :items="getAvailableRolesForUser(user)"
              item-text="name"
              item-value="id"
              label="Role"
              @change="onEditRoleChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
            
            <!-- Divisions field - read-only for self-editing, dropdown for others -->
            <v-text-field
              v-if="isEditingSelf"
              :value="getUserDivisions(editingUser).map(d => getDivisionName(d)).join(', ')"
              label="Divisions (cannot edit your own divisions)"
              readonly
              outlined
              prepend-icon="mdi-office-building"
              hint="Contact an admin to change your divisions"
              persistent-hint
            ></v-text-field>
            
            <v-select
              v-else
              v-model="editingUser.divisions"
              :items="availableDivisions"
              item-text="name"
              item-value="id"
              label="Divisions"
              multiple
              chips
              @change="onEditDivisionsChange"
            ></v-select>

            <!-- Groups field - read-only for self-editing, dropdown for others -->
            <v-text-field
              v-if="isEditingSelf"
              :value="getUserGroups(editingUser).map(g => getGroupName(g)).join(', ')"
              label="Groups (cannot edit your own groups)"
              readonly
              outlined
              prepend-icon="mdi-account-group"
              hint="Contact an admin to change your groups"
              persistent-hint
            ></v-text-field>
            
            <v-select
              v-else
              v-model="editingUser.groups"
              :items="getGroupsForDivisions(editingUser.divisions)"
              item-text="name"
              item-value="id"
              label="Groups"
              multiple
              chips
              :disabled="!editingUser.divisions || editingUser.divisions.length === 0"
              @change="onEditGroupsChange"
            ></v-select>



            <!-- Manager Status automatically determined by Role -->
            <v-alert
              v-if="editingUser.role === 'Manager'"
              type="info"
              outlined
              class="mt-4"
            >
              <strong>Manager Permissions:</strong> This user will automatically have manager permissions for all their assigned groups.
            </v-alert>

            <v-expansion-panels class="mt-4">

              <v-expansion-panel>
                <v-expansion-panel-header>Tool Permissions</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-if="isEditingSelf" class="mt-2">
                    <v-alert type="info" outlined>
                      <strong>Your Tool Permissions:</strong> Contact an admin to modify tool access.
                    </v-alert>
                    <div v-for="tool in getToolsForGroups(editingUser.groups)" :key="tool.id" class="mb-2 d-flex align-center">
                      <v-icon :color="editingUser.tool_permissions[tool.id]?.access ? 'green' : 'grey'" class="mr-3">
                        {{ editingUser.tool_permissions[tool.id]?.access ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                      <span>{{ tool.name }}</span>
                      <v-chip 
                        :color="editingUser.tool_permissions[tool.id]?.access ? 'green' : 'grey'" 
                        small 
                        class="ml-auto"
                      >
                        {{ editingUser.tool_permissions[tool.id]?.access ? 'Enabled' : 'Disabled' }}
                      </v-chip>
                    </div>
                  </div>
                  <div v-else-if="editingUser.role === 'Manager'" class="mt-2">
                    <v-alert type="success" outlined>
                      <strong>Manager Tool Access:</strong> This user automatically has access to all tools for their assigned groups.
                    </v-alert>
                    <div v-for="tool in getToolsForGroups(editingUser.groups)" :key="tool.id" class="mb-2 d-flex align-center">
                      <v-icon color="green" class="mr-3">
                        mdi-check-circle
                      </v-icon>
                      <span>{{ tool.name }}</span>
                      <v-chip 
                        color="green" 
                        small 
                        class="ml-auto"
                      >
                        Auto-Granted
                      </v-chip>
                    </div>
                  </div>
                  <div v-else-if="getToolsForGroups(editingUser.groups).length > 0" class="mt-2">
                    <v-alert type="info" outlined class="mb-3">
                      <strong>Individual Tool Access:</strong> Grant specific tool permissions for this {{ editingUser.role || 'user' }}.
                    </v-alert>
                    <div v-for="tool in getToolsForGroups(editingUser.groups)" :key="tool.id" class="mb-2">
                      <v-row align="center">
                        <v-col cols="8">
                          {{ tool.name }}
                        </v-col>
                        <v-col cols="4">
                          <v-switch
                            v-model="editingUser.tool_permissions[tool.id].access"
                            label="Access"
                            dense
                          ></v-switch>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                  <div v-else class="text--secondary">
                    No tools available for selected groups
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="isEditingSelf">
            <v-btn color="primary" @click="showEditDialog = false">Close</v-btn>
          </template>
          <template v-else>
            <v-btn text @click="showEditDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="saveUserEdit" :loading="savingUser">Save</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
   

    <!-- Success/Error Snackbars -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore'
import { DateTime } from 'luxon'
import { 
  getAllUsers, 
  inviteUser as inviteUserUtil, 
  updateUserRoles, 
  updateUserDivision,
  updateUserGroup,
  updateUserDivisions,
  updateUserGroups,
  updateToolPermissions,
  getUserByEmail,
  getAvailableDivisions,
  getGroupsForDivision,
  getToolsForGroup,
  getAllGroups,
  getDivisionName,
  getGroupName,
  TOOLS_CONFIG
} from '@/utils/userManagement'
import { 
  getRoleLevel,
  getUserRoleLevel,
  userHasRoleLevel,
  getUserRoleConfig,
  userCanInviteUsers,
  userCanManageUsers,
  getUserMaxAssignableRole,
  ROLES_CONFIG,
  AVAILABLE_ROLES
} from '@/config/toolsConfig'

export default {
  name: 'UserManagement',
  props: ['user'],
  data() {
    return {
      // User management
      allUsers: [],
      loadingUsers: false,
      userSearch: '',
      userStats: {
        total: 0,
        inactive: 0,
        active: 0,
        admins: 0
      },
      
      // Forms
      showInviteDialog: false,
      showEditDialog: false,
      inviteFormValid: false,
      editFormValid: false,
      invitingUser: false,
      savingUser: false,
      editingUser: null,
      
      inviteForm: {
        email: '',
        role: 'Employee', // Default to Employee
        divisions: [],
        groups: [],
        toolPermissions: {}
      },
      
      // Data
      availableDivisions: getAvailableDivisions(),
      allGroups: getAllGroups(),
      availableRoles: AVAILABLE_ROLES,
      toolsConfig: TOOLS_CONFIG,
      
      // UI
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      
      // Table headers
      userHeaders: [
        { text: 'Email', value: 'email' },
        { text: 'Role', value: 'role' },
        { text: 'Division', value: 'division' },
        { text: 'Group', value: 'group' },
        { text: 'Last Login', value: 'last_login' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      
      // Validation rules
      rules: {
        required: value => !!value || 'Required',
        email: value => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return pattern.test(value) || 'Invalid email'
        }
      }
    }
  },
  
  computed: {
    availableGroupsForDivisions() {
      if (!this.inviteForm.divisions || this.inviteForm.divisions.length === 0) return []
      return this.getGroupsForDivisions(this.inviteForm.divisions)
    },
    
    availableToolsForGroups() {
      if (!this.inviteForm.groups || this.inviteForm.groups.length === 0) return []
      return this.getToolsForGroups(this.inviteForm.groups)
    },
    
    isEditingSelf() {
      return this.editingUser && this.user && this.editingUser.email === this.user.email
    },
    
    visibleUsers() {
      if (!this.user) return []
      
      // Admins can see all users
      if (this.user.role === 'Admin') {
        return this.allUsers
      }
      
      // Managers can only see users in their groups
      if (this.user.role === 'Manager' && this.user.groups) {
        return this.allUsers.filter(user => {
          // Always include the current user themselves
          if (user.email === this.user.email) return true
          
          // Include users who share at least one group
          if (user.groups && Array.isArray(user.groups)) {
            return user.groups.some(userGroup => this.user.groups.includes(userGroup))
          }
          
          // Legacy support - check single group field
          if (user.group && this.user.groups.includes(user.group)) {
            return true
          }
          
          return false
        })
      }
      
      // Other roles (Employee, Pioneer) cannot access user management
      return []
    },
    
    filteredUsers() {
      if (!this.userSearch) return this.visibleUsers
      
      return this.visibleUsers.filter(user =>
        user.email.toLowerCase().includes(this.userSearch.toLowerCase())
      )
    }
  },
  
  created() {
    this.initializeInviteForm()
    this.loadAllUsers()
  },
  
  methods: {
    // Helper methods
    getGroupsForDivision,
    getToolsForGroup,
    // Role hierarchy methods
    getRoleLevel,
    getUserRoleLevel,
    userHasRoleLevel,
    getUserRoleConfig,
    userCanInviteUsers,
    userCanManageUsers,
    getUserMaxAssignableRole,
    
    // Multi-division/group helpers
    getUserDivisions(user) {
      return user.divisions || (user.division ? [user.division] : [])
    },
    
    getUserGroups(user) {
      return user.groups || (user.group ? [user.group] : [])
    },
    
    getGroupsForDivisions(divisions) {
      if (!divisions || divisions.length === 0) return []
      const allGroups = []
      divisions.forEach(divisionId => {
        const groups = getGroupsForDivision(divisionId)
        allGroups.push(...groups)
      })
      // Remove duplicates
      return allGroups.filter((group, index, self) => 
        index === self.findIndex(g => g.id === group.id)
      )
    },
    
    getToolsForGroups(groups) {
      if (!groups || groups.length === 0) return []
      const allTools = []
      groups.forEach(groupId => {
        const tools = getToolsForGroup(groupId)
        allTools.push(...tools)
      })
      // Remove duplicates
      return allTools.filter((tool, index, self) => 
        index === self.findIndex(t => t.id === tool.id)
      )
    },
    
    getDivisionName(divisionId) {
      return getDivisionName(divisionId)
    },
    
    getGroupName(groupId) {
      return getGroupName(groupId)
    },
    
    // Role helper methods
    getRoleName(roleId) {
      if (!roleId && roleId !== '') return 'Inactive'
      const role = ROLES_CONFIG.roles.find(r => r.id === roleId)
      return role ? role.name : roleId
    },
    
    getRoleColor(roleId) {
      switch (roleId) {
        case '': return 'grey'
        case 'Employee': return 'blue'
        case 'Pioneer': return 'purple'
        case 'Manager': return 'orange'
        case 'Admin': return 'red'
        default: return 'grey'
      }
    },
    
    getAvailableRolesForUser(currentUser) {
      // During migration period: if user has old roles array with Admin, treat as Admin
      const hasLegacyAdminRole = currentUser?.roles && currentUser.roles.includes('Admin')
      if (hasLegacyAdminRole) {
        return AVAILABLE_ROLES // Allow all roles for legacy Admins
      }
      
      const maxRole = getUserMaxAssignableRole(currentUser)
      if (!maxRole) return AVAILABLE_ROLES.filter(r => r.id === 'Employee') // Can only assign Employee
      
      const maxLevel = getRoleLevel(maxRole)
      return AVAILABLE_ROLES.filter(role => getRoleLevel(role.id) <= maxLevel)
    },
    
    initializeInviteForm() {
      this.inviteForm = {
        email: '',
        role: 'Employee', // Default to Employee
        divisions: [],
        groups: [],
        toolPermissions: {}
      }
    },
    
    onDivisionsChange() {
      // CLEAN SLATE: Reset groups and tool permissions when divisions change
      this.inviteForm.groups = []
      this.inviteForm.toolPermissions = {}
    },
    
    onGroupsChange() {
      // CLEAN SLATE: Reset and initialize tool permissions for the new groups only
      this.inviteForm.toolPermissions = {}
      const tools = this.getToolsForGroups(this.inviteForm.groups)
      tools.forEach(tool => {
        // For Managers, auto-grant access; for others, default to false
        const defaultAccess = this.inviteForm.role === 'Manager'
        this.inviteForm.toolPermissions[tool.id] = { access: defaultAccess }
      })
    },
    
    onRoleChange() {
      // Update tool permissions when role changes in invite form
      const tools = this.getToolsForGroups(this.inviteForm.groups)
      tools.forEach(tool => {
        if (this.inviteForm.toolPermissions[tool.id]) {
          const defaultAccess = this.inviteForm.role === 'Manager'
          this.inviteForm.toolPermissions[tool.id].access = defaultAccess
        }
      })
    },
    
    onEditRoleChange() {
      // Update tool permissions when role changes in edit form
      const tools = this.getToolsForGroups(this.editingUser.groups)
      tools.forEach(tool => {
        if (this.editingUser.tool_permissions[tool.id]) {
          const defaultAccess = this.editingUser.role === 'Manager'
          this.editingUser.tool_permissions[tool.id].access = defaultAccess
        }
      })
    },
    
    onEditDivisionsChange() {
      // CLEAN SLATE: Reset groups and tool permissions when divisions change in edit mode
      this.editingUser.groups = []
      this.editingUser.tool_permissions = {}
    },
    
    onEditGroupsChange() {
      // CLEAN SLATE: Reset tool permissions to only include tools for current groups
      const tools = this.getToolsForGroups(this.editingUser.groups)
      const newToolPermissions = {}
      
      tools.forEach(tool => {
        // Preserve existing permission if it exists, otherwise set default
        const existingPermission = this.editingUser.tool_permissions[tool.id]
        if (existingPermission) {
          newToolPermissions[tool.id] = existingPermission
        } else {
          // For Managers, auto-grant access; for others, default to false
          const defaultAccess = this.editingUser.role === 'Manager'
          newToolPermissions[tool.id] = { access: defaultAccess }
        }
      })
      
      // Replace the entire tool_permissions object with cleaned version
      this.editingUser.tool_permissions = newToolPermissions
    },
    
    async loadAllUsers() {
      this.loadingUsers = true
      try {
        this.allUsers = await getAllUsers()
        this.calculateUserStats()
      } catch (error) {
        this.showError('Error loading users: ' + error.message)
      }
      this.loadingUsers = false
    },
    
    calculateUserStats() {
      const users = this.visibleUsers
      this.userStats = {
        total: users.length,
        inactive: users.filter(u => !u.role || u.role === '').length,
        active: users.filter(u => u.role && u.role !== '').length,
        admins: users.filter(u => u.role === 'Admin').length
      }
    },
    
    async inviteUser() {
      if (!this.$refs.inviteForm.validate()) return
      
      this.invitingUser = true
      try {
        // Prepare tool permissions based on role
        let toolPermissions = this.inviteForm.toolPermissions
        
        // Managers get automatic access to all group tools
        if (this.inviteForm.role === 'Manager') {
          toolPermissions = {}
          const tools = this.getToolsForGroups(this.inviteForm.groups)
          tools.forEach(tool => {
            toolPermissions[tool.id] = { access: true }
          })
        }
        
        await inviteUserUtil(
          this.inviteForm.email,
          this.inviteForm.role,
          toolPermissions,
          this.inviteForm.divisions,
          this.inviteForm.groups,
          this.user.email
        )
        
        this.showSuccess('User invited successfully!')
        this.showInviteDialog = false
        this.resetInviteForm()
        this.loadAllUsers()
      } catch (error) {
        this.showError('Error inviting user: ' + error.message)
      }
      this.invitingUser = false
    },
    
    editUser(user) {
      this.editingUser = { 
        ...user,
        divisions: this.getUserDivisions(user),
        groups: this.getUserGroups(user),
        tool_permissions: user.tool_permissions || {}
      }
      
      // Manager status is now automatically determined by role
      
      // Ensure all tools for current groups have permission objects
      if (this.editingUser.groups && this.editingUser.groups.length > 0) {
        const tools = this.getToolsForGroups(this.editingUser.groups)
        tools.forEach(tool => {
          if (!this.editingUser.tool_permissions[tool.id]) {
            this.editingUser.tool_permissions[tool.id] = { access: false }
          }
        })
      }
      
      this.showEditDialog = true
    },
    
    async saveUserEdit() {
      // Security check: Users cannot edit their own information
      if (this.isEditingSelf) {
        this.showError('You cannot edit your own profile information. Contact an administrator.')
        return
      }
      
      this.savingUser = true
      try {
        await updateUserRoles(this.editingUser.email, this.editingUser.role)
        await updateUserDivisions(this.editingUser.email, this.editingUser.divisions)
        await updateUserGroups(this.editingUser.email, this.editingUser.groups)
        
        // Manager status is now automatically determined by role
        // Users with Role="Manager" are automatically managers of all their groups
        
        // CLEAN SLATE: Remove all existing tool permissions, then add only valid ones
        // This ensures no orphaned permissions remain from old groups
        
        if (this.editingUser.role === 'Manager') {
          // Managers get automatic access to all group tools
          const tools = this.getToolsForGroups(this.editingUser.groups)
          
          // Clear existing permissions and set only current group tools
          const managerPermissions = {}
          tools.forEach(tool => {
            managerPermissions[tool.id] = { access: true }
          })
          
          // Replace entire tool_permissions object in database
          await this.replaceAllToolPermissions(this.editingUser.email, managerPermissions)
        } else {
          // For other roles, use only the cleaned permissions from the UI
          // (already cleaned by onEditGroupsChange to only include current group tools)
          await this.replaceAllToolPermissions(this.editingUser.email, this.editingUser.tool_permissions)
        }
        

        
        this.showSuccess('User updated successfully!')
        this.showEditDialog = false
        this.loadAllUsers()
      } catch (error) {
        this.showError('Error updating user: ' + error.message)
      }
      this.savingUser = false
    },
    
    async deactivateUser(user) {
      // Security check: Users cannot deactivate themselves
      if (this.user && user.email === this.user.email) {
        this.showError('You cannot activate/deactivate your own account. Contact another administrator.')
        return
      }
      
      try {
        const isCurrentlyActive = user.role && user.role !== ''
        const newRole = isCurrentlyActive ? '' : 'Employee'
        
        await updateUserRoles(user.email, newRole)
        
        user.role = newRole
        this.showSuccess(`User ${isCurrentlyActive ? 'deactivated' : 'activated'}`)
        this.calculateUserStats()
      } catch (error) {
        this.showError('Error updating user role: ' + error.message)
      }
    },
    
    resetInviteForm() {
      this.initializeInviteForm()
    },
    
    async replaceAllToolPermissions(userEmail, newToolPermissions) {
      // ATOMIC OPERATION: Replace entire tool_permissions object to prevent orphaned permissions
      const db = getFirestore()
      const userRef = doc(db, 'users', userEmail)
      
      await updateDoc(userRef, {
        tool_permissions: newToolPermissions
      })
    },
    

    
    formatDate(date) {
      if (!date) return 'Never'
      
      // Handle Firestore timestamp format
      const timestamp = date.seconds ? date.seconds * 1000 : date
      const dt = DateTime.fromMillis(timestamp)
      
      // Check if date is invalid
      if (!dt.isValid) return 'Invalid date'
      
      const now = DateTime.now()
      const diffInHours = now.diff(dt, 'hours').hours
      
      // Show relative time for recent logins
      if (diffInHours < 1) {
        return dt.toRelative() // "3 minutes ago"
      } else if (diffInHours < 24) {
        return dt.toRelative() // "2 hours ago"
      } else if (diffInHours < 48) {
        return 'Yesterday at ' + dt.toFormat('h:mm a') // "Yesterday at 2:30 PM"
      } else if (diffInHours < 168) { // Within a week
        return dt.toFormat('cccc \'at\' h:mm a') // "Monday at 2:30 PM"
      } else {
        return dt.toFormat('MMM d, yyyy \'at\' h:mm a') // "Jan 15, 2024 at 2:30 PM"
      }
    },
    
    formatExactDate(date) {
      if (!date) return 'Never'
      
      // Handle Firestore timestamp format
      const timestamp = date.seconds ? date.seconds * 1000 : date
      const dt = DateTime.fromMillis(timestamp)
      
      if (!dt.isValid) return 'Invalid date'
      
      // Show exact date and time with timezone
      return dt.toFormat('DDDD \'at\' T ZZZZ') // "Monday, January 15, 2024 at 2:30:45 PM Eastern Standard Time"
    },
    
    showSuccess(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'success'
      this.showSnackbar = true
    },
    
    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.showSnackbar = true
    }
  }
}
</script> 